import { useMemo, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Button, Paper, List, ListItemButton, ListItemText, TextField,
  Switch, FormControlLabel, MenuItem, Grid, Chip, Alert, Snackbar, alpha, useTheme,
  InputAdornment, Divider, Drawer, useMediaQuery,
} from '@mui/material';
import {
  Tune, Palette, DirectionsCar, Payment, Notifications,
  Security, Stars, Subscriptions, Hub, Psychology, Save, Lock, Translate, Handshake, Menu, Storage,
  SystemUpdate,
} from '@mui/icons-material';
import SettingFileUpload from '../../components/settings/SettingFileUpload';
import { refreshTenantSession } from '../../hooks/useTenantBootstrap';
import {
  getSettingsCatalog, getSettings, bulkUpsertSettings,
  type SettingCategoryDef, type SettingRow, type SettingKeyDef,
} from '../../services/platform/settingsService';

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  general: <Tune fontSize="small" />,
  branding: <Palette fontSize="small" />,
  transport: <DirectionsCar fontSize="small" />,
  payment: <Payment fontSize="small" />,
  notification: <Notifications fontSize="small" />,
  security: <Security fontSize="small" />,
  loyalty: <Stars fontSize="small" />,
  subscription: <Subscriptions fontSize="small" />,
  integration: <Hub fontSize="small" />,
  ai: <Psychology fontSize="small" />,
  partner_onboarding: <Handshake fontSize="small" />,
  locale: <Translate fontSize="small" />,
  storage: <Storage fontSize="small" />,
  mobile_versioning: <SystemUpdate fontSize="small" />,
};

function SettingFieldInput({ def, value, onChange, category, onFileUploaded }: {
  def: SettingKeyDef;
  value: string;
  onChange: (v: string) => void;
  category: string;
  onFileUploaded?: () => void;
}) {
  if (def.type === 'file' || def.type === 'image') {
    return (
      <SettingFileUpload
        category={category}
        settingKey={def.key}
        label={def.label}
        description={def.description}
        value={value}
        accept={def.accept}
        onChange={onChange}
        onUploaded={() => onFileUploaded?.()}
      />
    );
  }
  if (def.type === 'boolean') {
    return (
      <FormControlLabel
        control={<Switch checked={value === 'true'} onChange={(e) => onChange(e.target.checked ? 'true' : 'false')} />}
        label={def.label}
      />
    );
  }
  if (def.type === 'select') {
    return (
      <TextField select fullWidth label={def.label} value={value} onChange={(e) => onChange(e.target.value)} helperText={def.description} size="small">
        {(def.options || []).map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
      </TextField>
    );
  }
  if (def.type === 'color') {
    return (
      <TextField
        fullWidth
        label={def.label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        helperText={def.description}
        size="small"
        type="color"
        InputLabelProps={{ shrink: true }}
      />
    );
  }
  return (
    <TextField
      fullWidth
      label={def.label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      helperText={def.description}
      size="small"
      type={def.type === 'password' ? 'password' : def.type === 'number' ? 'number' : def.type === 'email' ? 'email' : 'text'}
      InputProps={def.isEncrypted ? { startAdornment: <InputAdornment position="start"><Lock fontSize="small" /></InputAdornment> } : undefined}
    />
  );
}

export default function SettingsPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();
  const [catalogOpen, setCatalogOpen] = useState(false);
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedCategory = searchParams.get('category') || 'general';
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [snack, setSnack] = useState('');

  const { data: catalogRaw = [] } = useQuery({ queryKey: ['settings-catalog'], queryFn: getSettingsCatalog });
  const catalog = useMemo(
    () => (catalogRaw as SettingCategoryDef[]).filter(
      (c) => c.key !== 'mobile_versioning' && c.key !== 'food' && c.key !== 'grocery',
    ),
    [catalogRaw],
  );
  const { data: settings = [] } = useQuery({ queryKey: ['settings'], queryFn: () => getSettings() });

  const activeCategory = useMemo(() => {
    if (!catalog.length) return requestedCategory;
    return catalog.some((c: SettingCategoryDef) => c.key === requestedCategory)
      ? requestedCategory
      : 'general';
  }, [catalog, requestedCategory]);

  useEffect(() => {
    if (catalog.length && requestedCategory !== activeCategory) {
      setSearchParams({ category: activeCategory }, { replace: true });
    }
  }, [catalog.length, requestedCategory, activeCategory, setSearchParams]);

  const categoryDef = useMemo(
    () => catalog.find((c: SettingCategoryDef) => c.key === activeCategory),
    [catalog, activeCategory]
  );

  const settingsMap = useMemo(() => {
    const map = new Map<string, SettingRow>();
    settings.forEach((s: SettingRow) => map.set(`${s.category}.${s.key}`, s));
    return map;
  }, [settings]);

  const getValue = (category: string, key: string, defaultValue?: string) => {
    const draftKey = `${category}.${key}`;
    if (draft[draftKey] !== undefined) return draft[draftKey];
    const stored = settingsMap.get(draftKey);
    return stored?.value ?? defaultValue ?? '';
  };

  const setValue = (category: string, key: string, value: string) => {
    setDraft((p) => ({ ...p, [`${category}.${key}`]: value }));
  };

  const configuredCount = useMemo(() => settings.filter((s: SettingRow) => s.hasValue).length, [settings]);
  const totalKeys = useMemo(() => catalog.reduce((n: number, c: SettingCategoryDef) => n + c.keys.length, 0), [catalog]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const items = Object.entries(draft).map(([compound, value]) => {
        const [category, key] = compound.split('.');
        return { category, key, value };
      });
      if (!items.length) return [];
      return bulkUpsertSettings(items);
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      setDraft({});
      setSnack('Settings saved successfully');
      await refreshTenantSession(dispatch);
    },
  });

  const categoryDraftCount = categoryDef?.keys.filter((k: SettingKeyDef) => draft[`${activeCategory}.${k.key}`] !== undefined).length || 0;

  const categoryList = (
    <>
      <Box sx={{ p: 2, background: `linear-gradient(135deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.dark})`, color: 'white' }}>
        <Typography variant="subtitle1" fontWeight={700}>Settings Catalog</Typography>
        <Typography variant="caption" sx={{ opacity: 0.9 }}>{configuredCount}/{totalKeys} configured</Typography>
      </Box>
      <List dense disablePadding>
        {catalog.map((cat: SettingCategoryDef) => (
          <ListItemButton
            key={cat.key}
            selected={activeCategory === cat.key}
            onClick={() => {
              setSearchParams({ category: cat.key });
              setCatalogOpen(false);
            }}
            sx={{ borderLeft: activeCategory === cat.key ? 3 : 0, borderColor: 'secondary.main' }}
          >
            <Box mr={1} color="secondary.main">{CATEGORY_ICONS[cat.key] || <Tune fontSize="small" />}</Box>
            <ListItemText
              primary={cat.label}
              secondary={`${cat.keys.length} keys`}
              primaryTypographyProps={{ fontWeight: 600, fontSize: 13 }}
            />
          </ListItemButton>
        ))}
      </List>
    </>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, minHeight: 'calc(100vh - 180px)' }}>
      {isMobile ? (
        <>
          <Button
            variant="outlined"
            startIcon={<Menu />}
            onClick={() => setCatalogOpen(true)}
            sx={{ alignSelf: 'flex-start' }}
          >
            {categoryDef?.label || 'Settings'}
          </Button>
          <Drawer anchor="left" open={catalogOpen} onClose={() => setCatalogOpen(false)}>
            <Box sx={{ width: 280 }}>{categoryList}</Box>
          </Drawer>
        </>
      ) : (
        <Paper elevation={0} sx={{ width: 280, flexShrink: 0, borderRadius: 3, border: 1, borderColor: 'divider', overflow: 'hidden' }}>
          {categoryList}
        </Paper>
      )}

      <Box flex={1} minWidth={0}>
        <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, mb: 2, borderRadius: 3, border: 1, borderColor: 'divider', background: alpha(theme.palette.secondary.main, 0.04) }}>
          <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2}>
            <Box>
              <Typography variant="h5" fontWeight={800}>{categoryDef?.label || activeCategory}</Typography>
              <Typography variant="body2" color="text.secondary">{categoryDef?.description}</Typography>
              <Box display="flex" gap={1} mt={1}>
                <Chip size="small" label={`${categoryDef?.keys.length || 0} setting keys`} />
                {categoryDraftCount > 0 && <Chip size="small" color="warning" label={`${categoryDraftCount} unsaved`} />}
              </Box>
            </Box>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<Save />}
              disabled={Object.keys(draft).length === 0 || saveMutation.isPending}
              onClick={() => saveMutation.mutate()}
              sx={{ alignSelf: { xs: 'stretch', sm: 'auto' } }}
            >
              Save Changes
            </Button>
          </Box>
        </Paper>

        <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, border: 1, borderColor: 'divider' }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            Upload logo and favicon directly — files are stored locally by default. Enable S3 under <strong>File Storage</strong> to store uploads in your client&apos;s S3 bucket.
          </Alert>
          <Grid container spacing={2.5}>
            {categoryDef?.keys.map((keyDef: SettingKeyDef) => {
              const stored = settingsMap.get(`${activeCategory}.${keyDef.key}`);
              return (
                <Grid item xs={12} md={6} key={keyDef.key}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%', bgcolor: stored?.hasValue ? alpha(theme.palette.success.main, 0.04) : 'background.paper' }}>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="caption" color="text.secondary" fontFamily="monospace">{keyDef.key}</Typography>
                      {stored?.hasValue ? <Chip label="Configured" size="small" color="success" /> : <Chip label="Default" size="small" variant="outlined" />}
                    </Box>
                    <SettingFieldInput
                      def={keyDef}
                      category={activeCategory}
                      value={getValue(activeCategory, keyDef.key, keyDef.defaultValue)}
                      onChange={(v) => setValue(activeCategory, keyDef.key, v)}
                      onFileUploaded={() => {
                        queryClient.invalidateQueries({ queryKey: ['settings'] });
                        setDraft((prev) => {
                          const next = { ...prev };
                          delete next[`${activeCategory}.${keyDef.key}`];
                          return next;
                        });
                        setSnack('File uploaded and saved');
                        if (activeCategory === 'branding') {
                          void refreshTenantSession(dispatch);
                        }
                      }}
                    />
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
          <Divider sx={{ my: 3 }} />
          <Typography variant="caption" color="text.secondary">
            Category: <strong>{activeCategory}</strong> · Changes apply per tenant and take effect immediately for new requests.
          </Typography>
        </Paper>
      </Box>

      <Snackbar open={!!snack} autoHideDuration={3000} onClose={() => setSnack('')} message={snack} />
    </Box>
  );
}
